import React, {useEffect, useMemo, useRef} from "react";
import {useLocation} from "react-router-dom";
import "./index.css";
import {useSetting} from "contexts/setting";
import {palettes} from "data/palettes";
import {toOKLCH} from "libs/color";
import {randomFloor} from "libs/utils";
import {readGenerativeFrame} from "libs/generativeFrame";
import {isRenderTimelineControlled, subscribeRenderTimeline} from "libs/renderTimeline";

// ---------------------------------------------------------------------------
// Hue-safety: detect complementary palette pairs and shift hues at runtime
// when their gradient blobs overlap, to prevent muddy desaturated mixes.
// ---------------------------------------------------------------------------

function hueDistance(h1, h2) {
  const d = Math.abs(h1 - h2);
  return Math.min(d, 360 - d);
}

/**
 * For each pair of palette colors whose hues are >120° apart (complements),
 * find the "bridge" hue — the palette color that sits between them.
 * When complements overlap, the further blob shifts toward the bridge.
 */
function buildColorMeta(oklchCols) {
  const hues = oklchCols.map((c) => parseFloat(c.h));
  const conflictPairs = [];

  for (let a = 0; a < hues.length; a++) {
    for (let b = a + 1; b < hues.length; b++) {
      if (hueDistance(hues[a], hues[b]) > 120) {
        // Find bridge: the palette color that is neither a nor b
        let bridgeHue = (hues[a] + hues[b]) / 2; // fallback: midpoint
        for (let k = 0; k < hues.length; k++) {
          if (k === a || k === b) continue;
          bridgeHue = hues[k];
          break;
        }
        conflictPairs.push({a, b, bridgeHue});
      }
    }
  }
  return {hues, conflictPairs};
}

// Spatial thresholds (percentage-point units between blob centers).
// These are moderate: shifting starts at 75 and reaches full at 30.
const SAFE_DIST = 75;
const FULL_SHIFT_DIST = 30;

function lerpHue(from, to, t) {
  let diff = to - from;
  if (diff > 180) diff -= 360;
  if (diff < -180) diff += 360;
  return (((from + diff * t) % 360) + 360) % 360;
}

// ---------------------------------------------------------------------------
// Pure helpers (module scope — no component state dependency)
// ---------------------------------------------------------------------------

const generatePositions = (gradientCount, stopCount) => {
  const positions = [];
  for (let i = 0; i < gradientCount; i++) {
    const stops = [];
    for (let s = 0; s < stopCount; s++) {
      const range = s === 0 ? [-70, 170] : [-50, 150];
      stops.push({x: randomFloor(range[0], range[1]), y: randomFloor(range[0], range[1])});
    }
    positions.push(stops);
  }
  return positions;
};

const generateColors = (count, palette) => {
  const colors = [];
  for (let i = 0; i < count; i++) {
    if (i < palette.length) {
      colors.push(palette[i]);
    } else {
      const idx = Math.floor(Math.random() * palette.length);
      const diff = randomFloor(-10, 10);
      const col = {...palette[idx], h: `${parseFloat(palette[idx].h) + diff}`};
      colors.push(col);
    }
  }
  return colors;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const GenerativeBg = () => {
  const {
    product,
    generativeGradients,
    editGenerativeGradients,
    isAnimationPaused,
    generativeSpeed,
    generativeCount,
    generativeStops,
    regenerateKey,
    generativeExportFrame,
    registerGenerativeFrameReader,
  } = useSetting();
  const location = useLocation();
  const isRenderMode = new URLSearchParams(location.search).get("render") === "true";
  const oklchCols = useMemo(
    () => palettes[product.label]?.map((color) => toOKLCH(color)) ?? [],
    [product.label]
  );
  // A scene or default that applies later can set the gradients to null.
  // The key is a dependency, so the effect then makes new gradients.
  const loadedPaletteKey = generativeGradients?.paletteKey;
  const containerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const startTimeRef = useRef(null);
  const pausedTimeRef = useRef(0);

  // ------ Generate gradients once and store in context ------
  useEffect(() => {
    if (!oklchCols || oklchCols.length === 0) return;

    const key = `${product.label}:${generativeCount}:${generativeStops}:${regenerateKey}`;
    if (loadedPaletteKey === key) return;

    const amount = generativeCount > 0 ? generativeCount : randomFloor(6, 15);
    const stopCount = generativeStops;
    const colors = generateColors(amount, oklchCols);
    const positions = generatePositions(amount, stopCount);

    // Assign each blob to its closest palette color (for conflict checking)
    const assignments = colors.map((color) => {
      const h = parseFloat(color.h);
      let closest = 0;
      let minDist = Infinity;
      for (let p = 0; p < oklchCols.length; p++) {
        const d = hueDistance(h, parseFloat(oklchCols[p].h));
        if (d < minDist) {
          minDist = d;
          closest = p;
        }
      }
      return closest;
    });

    const colorMeta = buildColorMeta(oklchCols);
    const originalHues = colors.map((c) => parseFloat(c.h));

    const gradients = [];
    const cssProperties = {};
    const animName = `move-${Date.now()}`;

    const stopPercentages = Array.from({length: stopCount}, (_, s) =>
      Math.round((s / (stopCount - 1)) * 100)
    );
    const keyframeBlocks = stopPercentages.map(() => "");

    for (let i = 0; i < amount; i++) {
      const idx = i + 1;
      const stops = positions[i];
      const {l, c, h} = colors[i];
      const hueVal = parseFloat(h);

      cssProperties[`--x${idx}`] = `${stops[0].x}%`;
      cssProperties[`--y${idx}`] = `${stops[0].y}%`;
      cssProperties[`--h${idx}`] = `${hueVal}`;

      for (let s = 0; s < stopCount; s++) {
        keyframeBlocks[s] += `    --x${idx}: ${stops[s].x}%; --y${idx}: ${stops[s].y}%;\n`;
      }

      // Use var(--h{idx}) for hue so the animation loop can shift it at runtime
      gradients.push(
        `radial-gradient(circle at var(--x${idx}) var(--y${idx}), oklch(${l} ${c} var(--h${idx}) / 80%) 0px, oklch(${l} ${c} var(--h${idx}) / 60%) 40%, transparent 70%)`
      );
    }

    const keyframesStr = `@keyframes ${animName} {\n${stopPercentages
      .map((pct, s) => `  ${pct}% {\n${keyframeBlocks[s]}  }`)
      .join("\n")}\n}`;

    editGenerativeGradients({
      paletteKey: key,
      gradients,
      cssProperties,
      keyframes: keyframesStr,
      animationName: animName,
      positions,
      colorMeta: {
        assignments,
        originalHues,
        conflictPairs: colorMeta.conflictPairs,
      },
    });
  }, [
    oklchCols,
    product.label,
    generativeCount,
    generativeStops,
    regenerateKey,
    loadedPaletteKey,
    editGenerativeGradients,
  ]);

  // ------ Read gradient config ------
  const {gradients, cssProperties, keyframes, positions, colorMeta} = generativeGradients || {
    gradients: [],
    cssProperties: {},
    keyframes: "",
    positions: [],
    colorMeta: null,
  };

  useEffect(() => {
    if (keyframes) {
      const styleId = "generative-bg-keyframes";
      let el = document.getElementById(styleId);
      if (!el) {
        el = document.createElement("style");
        el.id = styleId;
        document.head.appendChild(el);
      }
      el.textContent = keyframes;
    }
  }, [keyframes]);

  const backgroundImage = gradients.join(",\n    ");
  const frozenExportFrame = isRenderMode ? generativeExportFrame : null;

  useEffect(() => {
    return registerGenerativeFrameReader(() => {
      if (!containerRef.current) return null;
      return readGenerativeFrame(containerRef.current.style, positions.length, cssProperties);
    });
  }, [registerGenerativeFrameReader, positions.length, cssProperties]);

  // ------ Animation loop with runtime hue shifting ------
  useEffect(() => {
    if (!containerRef.current || !positions || positions.length === 0) return;
    if (frozenExportFrame) return;

    const stopCount = positions[0]?.length || 3;
    const segmentCount = stopCount - 1;
    const {assignments, originalHues, conflictPairs} = colorMeta || {};
    const hasConflicts = conflictPairs?.length > 0;

    const renderAtTime = (elapsed) => {
      if (!containerRef.current) return;
      // generativeSpeed is the full ping-pong loop period (forward + back). One
      // sweep is therefore half of it. Defining it this way means a recording
      // of length generativeSpeed captures exactly one full cycle, so exported
      // video/GIF loops seamlessly (first frame == last frame).
      const duration = generativeSpeed / 2;
      const totalDuration = duration * 2;
      const progress = (elapsed % totalDuration) / duration;
      const t = progress <= 1 ? progress : 2 - progress;

      // 1. Compute current positions for all blobs
      const currentPositions = [];
      for (let i = 0; i < positions.length; i++) {
        const idx = i + 1;
        const stops = positions[i];
        const segFloat = t * segmentCount;
        const seg = Math.min(Math.floor(segFloat), segmentCount - 1);
        const localT = segFloat - seg;
        const from = stops[seg];
        const to = stops[seg + 1];
        const cx = from.x + (to.x - from.x) * localT;
        const cy = from.y + (to.y - from.y) * localT;
        currentPositions.push({x: cx, y: cy});
        containerRef.current.style.setProperty(`--x${idx}`, `${cx}%`);
        containerRef.current.style.setProperty(`--y${idx}`, `${cy}%`);
      }

      // 2. Set hues for all blobs (unconditional — required even without conflicts)
      if (originalHues) {
        for (let i = 0; i < positions.length; i++) {
          containerRef.current.style.setProperty(`--h${i + 1}`, `${originalHues[i]}`);
        }
      }

      // 3. Override hues when complement blobs overlap
      if (hasConflicts && assignments && originalHues) {
        const shiftFactors = new Float32Array(positions.length);

        for (const {a: palA, b: palB, bridgeHue} of conflictPairs) {
          for (let i = 0; i < positions.length; i++) {
            for (let j = i + 1; j < positions.length; j++) {
              const gi = assignments[i];
              const gj = assignments[j];
              if (!((gi === palA && gj === palB) || (gi === palB && gj === palA))) continue;

              const dx = currentPositions[i].x - currentPositions[j].x;
              const dy = currentPositions[i].y - currentPositions[j].y;
              const dist = Math.sqrt(dx * dx + dy * dy);

              if (dist < SAFE_DIST) {
                const factor = Math.min(1, (SAFE_DIST - dist) / (SAFE_DIST - FULL_SHIFT_DIST));
                const distI = hueDistance(originalHues[i], bridgeHue);
                const distJ = hueDistance(originalHues[j], bridgeHue);
                const shiftIdx = distI > distJ ? i : j;
                shiftFactors[shiftIdx] = Math.max(shiftFactors[shiftIdx], factor);
              }
            }
          }
        }

        for (let i = 0; i < positions.length; i++) {
          if (shiftFactors[i] > 0) {
            let targetBridge = originalHues[i];
            for (const {a: palA, b: palB, bridgeHue} of conflictPairs) {
              if (assignments[i] === palA || assignments[i] === palB) {
                targetBridge = bridgeHue;
                break;
              }
            }
            const newHue = lerpHue(originalHues[i], targetBridge, shiftFactors[i]);
            containerRef.current.style.setProperty(`--h${i + 1}`, `${newHue}`);
          }
        }
      }
    };

    // Server video export advances this animation with media time. Do not use
    // wall-clock requestAnimationFrame time while Playwright captures frames.
    if (isRenderTimelineControlled()) {
      renderAtTime(0);
      return subscribeRenderTimeline(renderAtTime);
    }

    const animate = (timestamp) => {
      if (!containerRef.current) return;
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp - pausedTimeRef.current;
      }

      renderAtTime(timestamp - startTimeRef.current);
      if (!isAnimationPaused) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };

    if (!isAnimationPaused) {
      animationFrameRef.current = requestAnimationFrame(animate);
    } else {
      if (startTimeRef.current) {
        pausedTimeRef.current = performance.now() - startTimeRef.current;
        startTimeRef.current = null;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isAnimationPaused, positions, generativeSpeed, colorMeta, frozenExportFrame]);

  return (
    <div className="image-wrapper">
      <div>
        <div
          ref={containerRef}
          className="gen-bg bg-image"
          style={{
            backgroundImage: backgroundImage,
            ...cssProperties,
            ...(frozenExportFrame || {}),
          }}
        ></div>
      </div>
    </div>
  );
};

export default GenerativeBg;
